import React, { useMemo, useState } from "react";
import "./LegalMcp.css";

const DEFAULT_ENDPOINT = "https://russian-law-mcp.fly.dev/mcp";
const MCP_PROTOCOL_VERSIONS = ["2025-03-26", "2024-11-05"];

const EXAMPLE_QUESTIONS = [
  "Какие требования есть к обработке персональных данных сотрудников?",
  "Можно ли расторгнуть договор оказания услуг в одностороннем порядке?",
  "Какая ответственность предусмотрена за нарушение сроков оплаты по договору?",
];

export default function LegalMcp() {
  const [endpoint, setEndpoint] = useState(DEFAULT_ENDPOINT);
  const [question, setQuestion] = useState(EXAMPLE_QUESTIONS[0]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);

  const calls = useMemo(() => (Array.isArray(result?.calls) ? result.calls : []), [result]);
  const searchItems = useMemo(() => getSearchItems(calls), [calls]);
  const provisionItems = useMemo(() => getProvisionItems(calls), [calls]);
  const sourcesItems = useMemo(() => getSourcesItems(calls), [calls]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const preparedQuestion = question.trim();
    if (!preparedQuestion) {
      setError("Введите вопрос для юридического MCP.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    setRawOpen(false);

    try {
      const research = await runLegalMcpResearch({
        endpoint,
        question: preparedQuestion,
      });

      setResult(research);
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Не удалось выполнить запрос к MCP."));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAnswer = async () => {
    const answerText = result?.answer?.text;
    if (!answerText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(answerText);
    } catch {
      setError("Браузер не дал доступ к буферу обмена.");
    }
  };

  return (
    <main className="legal-mcp-page">
      <header className="legal-mcp-header">
        <div>
          <p className="legal-mcp-kicker">Admin / Legal MCP</p>
          <h1>Юридический MCP</h1>
        </div>
        <div className="legal-mcp-endpoint-chip">{result?.mcpEndpoint || endpoint}</div>
      </header>

      <section className="legal-mcp-workspace">
        <form className="legal-mcp-panel legal-mcp-form" onSubmit={handleSubmit}>
          <label className="legal-mcp-field">
            <span>Вопрос</span>
            <textarea
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Например: какие нормы применяются к обработке персональных данных?"
              disabled={loading}
              rows={7}
            />
          </label>

          <div className="legal-mcp-examples">
            {EXAMPLE_QUESTIONS.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => setQuestion(example)}
                disabled={loading}
              >
                {example}
              </button>
            ))}
          </div>

          <label className="legal-mcp-field">
            <span>MCP endpoint</span>
            <input
              value={endpoint}
              onChange={(event) => setEndpoint(event.target.value)}
              placeholder={DEFAULT_ENDPOINT}
              disabled={loading}
            />
          </label>

          <div className="legal-mcp-note">
            Запрос идет прямо в hosted MCP endpoint, без backend route в админке.
          </div>

          {error && <div className="legal-mcp-alert legal-mcp-alert-error">{error}</div>}

          <div className="legal-mcp-actions">
            <button className="legal-mcp-button legal-mcp-button-primary" type="submit" disabled={loading}>
              {loading ? "Запрос выполняется..." : "Запустить исследование"}
            </button>
            <button
              className="legal-mcp-button"
              type="button"
              onClick={() => {
                setQuestion("");
                setResult(null);
                setError("");
              }}
              disabled={loading}
            >
              Очистить
            </button>
          </div>
        </form>

        <aside className="legal-mcp-panel legal-mcp-summary">
          <h2>Статус</h2>
          {result ? (
            <>
              <RiskBadge riskLevel={result?.risk?.riskLevel} />
              <dl className="legal-mcp-stats">
                <div>
                  <dt>Инструментов</dt>
                  <dd>{calls.length}</dd>
                </div>
                <div>
                  <dt>Норм</dt>
                  <dd>{provisionItems.length}</dd>
                </div>
                <div>
                  <dt>Поиск</dt>
                  <dd>{searchItems.length}</dd>
                </div>
                <div>
                  <dt>Ответ</dt>
                  <dd>{result?.answer?.status || "нет"}</dd>
                </div>
              </dl>

              {Array.isArray(result?.risk?.warnings) && result.risk.warnings.length > 0 && (
                <div className="legal-mcp-warning-list">
                  {result.risk.warnings.map((warning, index) => (
                    <div key={`${warning}-${index}`} className="legal-mcp-warning">
                      {warning}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="legal-mcp-empty">Нет активного результата.</div>
          )}
        </aside>
      </section>

      {result && (
        <section className="legal-mcp-results">
          <article className="legal-mcp-panel legal-mcp-answer">
            <div className="legal-mcp-section-head">
              <div>
                <h2>Черновик ответа</h2>
                <p>{result.question}</p>
              </div>
              <button className="legal-mcp-button" type="button" onClick={handleCopyAnswer}>
                Скопировать
              </button>
            </div>
            <pre>{result?.answer?.text || "Ответ не сформирован."}</pre>
          </article>

          <article className="legal-mcp-panel">
            <div className="legal-mcp-section-head">
              <div>
                <h2>Правовая позиция</h2>
                <p>{provisionItems.length} найденных норм</p>
              </div>
            </div>
            <ItemsList
              items={provisionItems}
              emptyText="MCP не вернул нормы для правовой позиции."
              variant="provision"
            />
          </article>

          <article className="legal-mcp-panel">
            <div className="legal-mcp-section-head">
              <div>
                <h2>Поиск по законодательству</h2>
                <p>{searchItems.length} результатов</p>
              </div>
            </div>
            <ItemsList
              items={searchItems}
              emptyText="Поиск не вернул результатов."
              variant="search"
            />
          </article>

          <article className="legal-mcp-panel">
            <div className="legal-mcp-section-head">
              <div>
                <h2>Источники и вызовы</h2>
                <p>{sourcesItems.length ? `${sourcesItems.length} источников` : `${calls.length} вызовов`}</p>
              </div>
              <button
                className="legal-mcp-button"
                type="button"
                onClick={() => setRawOpen((open) => !open)}
              >
                {rawOpen ? "Скрыть JSON" : "Показать JSON"}
              </button>
            </div>

            {sourcesItems.length > 0 && (
              <ItemsList
                items={sourcesItems}
                emptyText=""
                variant="source"
              />
            )}

            <div className="legal-mcp-call-list">
              {calls.map((call, index) => (
                <details key={`${call.tool}-${index}`} className="legal-mcp-call">
                  <summary>
                    <span>{call.tool}</span>
                    <span>{formatMs(call.durationMs)}</span>
                  </summary>
                  <pre>{JSON.stringify(call, null, 2)}</pre>
                </details>
              ))}
            </div>

            {rawOpen && <pre className="legal-mcp-raw">{JSON.stringify(result, null, 2)}</pre>}
          </article>
        </section>
      )}
    </main>
  );
}

function RiskBadge({ riskLevel }) {
  const normalizedRisk = riskLevel || "unknown";
  const labels = {
    low: "Низкий риск",
    medium: "Средний риск",
    high: "Высокий риск",
    unknown: "Риск не определен",
  };

  return (
    <div className={`legal-mcp-risk legal-mcp-risk-${normalizedRisk}`}>
      {labels[normalizedRisk] || normalizedRisk}
    </div>
  );
}

function ItemsList({ items, emptyText, variant }) {
  if (!items.length) {
    return emptyText ? <div className="legal-mcp-empty">{emptyText}</div> : null;
  }

  return (
    <div className="legal-mcp-item-list">
      {items.map((item, index) => (
        <article className="legal-mcp-item" key={`${variant}-${index}-${getItemTitle(item)}`}>
          <div className="legal-mcp-item-title">{getItemTitle(item)}</div>
          <div className="legal-mcp-item-meta">
            {getItemMeta(item, variant)}
          </div>
          {item.snippet && <p>{item.snippet}</p>}
          {item.text && typeof item.text === "string" && <p>{item.text}</p>}
          {item.url && (
            <a href={item.url} target="_blank" rel="noopener noreferrer">
              Открыть источник
            </a>
          )}
        </article>
      ))}
    </div>
  );
}

async function runLegalMcpResearch({ endpoint, question }) {
  const preparedEndpoint = endpoint.trim().replace(/\/$/, "");
  const client = await createMcpClient(preparedEndpoint);
  const calls = [];

  const about = await client.callTool("about");
  calls.push(about);

  const sources = await client.callTool("list_sources");
  calls.push(sources);

  const search = await client.callTool("search_legislation", {
    query: question,
    limit: 10,
  });
  calls.push(search);

  const stance = await client.callTool("build_legal_stance", {
    query: question,
    limit: 5,
  });
  calls.push(stance);

  const risk = detectRisk({
    question,
    searchResult: search,
    stanceResult: stance,
  });

  return {
    question,
    mcpEndpoint: preparedEndpoint,
    protocolVersion: client.protocolVersion,
    risk,
    answer: makeDemoAnswer(question, stance, risk),
    calls,
  };
}

async function createMcpClient(endpoint) {
  let lastError = null;

  for (const protocolVersion of MCP_PROTOCOL_VERSIONS) {
    try {
      let requestId = 1;
      let sessionId = "";

      const initialize = await sendMcpRequest({
        endpoint,
        request: {
          jsonrpc: "2.0",
          id: requestId,
          method: "initialize",
          params: {
            protocolVersion,
            capabilities: {},
            clientInfo: {
              name: "admin-legal-mcp",
              version: "1.0.0",
            },
          },
        },
        sessionId,
      });

      assertMcpSuccess(initialize.payload);
      sessionId = initialize.sessionId;

      await sendMcpRequest({
        endpoint,
        request: {
          jsonrpc: "2.0",
          method: "notifications/initialized",
          params: {},
        },
        sessionId,
      });

      return {
        protocolVersion,
        callTool: async (name, args = {}) => {
          requestId += 1;
          const startedAt = Date.now();
          const response = await sendMcpRequest({
            endpoint,
            request: {
              jsonrpc: "2.0",
              id: requestId,
              method: "tools/call",
              params: {
                name,
                arguments: args,
              },
            },
            sessionId,
          });

          assertMcpSuccess(response.payload);
          sessionId = response.sessionId;

          return {
            tool: name,
            args,
            durationMs: Date.now() - startedAt,
            result: parseMcpToolResult(response.payload.result),
          };
        },
      };
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("MCP initialize failed");
}

async function sendMcpRequest({ endpoint, request, sessionId }) {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json, text/event-stream",
  };

  if (sessionId) {
    headers["mcp-session-id"] = sessionId;
  }

  const response = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify(request),
  });

  const payload = await parseMcpHttpResponse(response);
  const nextSessionId = response.headers.get("mcp-session-id") || sessionId;

  return {
    payload,
    sessionId: nextSessionId,
  };
}

async function parseMcpHttpResponse(response) {
  const text = await response.text();

  if (!response.ok) {
    throw new Error(text || `MCP HTTP ${response.status}`);
  }

  if (!text.trim()) {
    return null;
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("text/event-stream") || text.includes("data:")) {
    return parseSseJson(text);
  }

  return JSON.parse(text);
}

function parseSseJson(text) {
  const messages = [];
  let buffer = "";

  text.split(/\r?\n/).forEach((line) => {
    if (line.startsWith("data:")) {
      buffer += line.slice(5).trim();
      return;
    }

    if (!line.trim() && buffer) {
      messages.push(JSON.parse(buffer));
      buffer = "";
    }
  });

  if (buffer) {
    messages.push(JSON.parse(buffer));
  }

  const responseMessage = [...messages]
    .reverse()
    .find((message) => message?.result || message?.error || message?.id);

  if (!responseMessage) {
    throw new Error("MCP вернул пустой SSE response");
  }

  return responseMessage;
}

function assertMcpSuccess(payload) {
  if (payload?.error) {
    throw new Error(payload.error.message || JSON.stringify(payload.error));
  }
}

function parseMcpToolResult(result) {
  const textContent = result?.content?.find((item) => item.type === "text");

  if (!textContent?.text) {
    return result;
  }

  try {
    return JSON.parse(textContent.text);
  } catch {
    return textContent.text;
  }
}

function detectRisk({ question, searchResult, stanceResult }) {
  const warnings = [];
  let riskLevel = "low";

  const searchItems = searchResult?.result?.results || [];
  const stanceItems = stanceResult?.result?.results?.provisions || [];
  const allItems = [...searchItems, ...stanceItems];

  const amendmentItems = allItems.filter((item) => {
    const title = (item.document_title || "").toLowerCase();
    return (
      title.includes("о внесении изменений") ||
      title.includes("внесении изменений") ||
      title.includes("признании утратив")
    );
  });

  if (amendmentItems.length > 0) {
    warnings.push(
      "Среди результатов есть законы о внесении изменений. Их нельзя автоматически считать основным законом."
    );
    riskLevel = "medium";
  }

  const normalizedQuestion = question.toLowerCase();
  const mentionsPersonalData =
    question.includes("152") ||
    normalizedQuestion.includes("персональн") ||
    normalizedQuestion.includes("данн");

  const suspicious149Content = allItems.some((item) => {
    const title = item.title || "";
    const snippet = item.snippet || "";

    return (
      title.includes("Обладатель информации") ||
      snippet.includes("Обладатель информации") ||
      snippet.includes("Общедоступная информация")
    );
  });

  if (mentionsPersonalData && suspicious149Content) {
    warnings.push(
      "Вопрос похож на 152-ФЗ о персональных данных, но часть найденных фрагментов похожа на 149-ФЗ об информации. Нужна ручная проверка."
    );
    riskLevel = "high";
  }

  const metadata =
    searchResult?.result?._metadata ||
    stanceResult?.result?._metadata ||
    null;

  const stalenessWarning = metadata?.data_freshness?.staleness_warning;

  if (stalenessWarning) {
    warnings.push(stalenessWarning);
    if (riskLevel === "low") {
      riskLevel = "medium";
    }
  }

  if (allItems.length === 0) {
    warnings.push("MCP не нашел релевантные нормы.");
    riskLevel = "high";
  }

  return {
    riskLevel,
    warnings,
    answerAllowed: riskLevel !== "high",
  };
}

function makeDemoAnswer(question, stanceResult, risk) {
  const provisions = stanceResult?.result?.results?.provisions || [];

  if (risk.riskLevel === "high") {
    return {
      status: "blocked",
      text:
        "Ответ заблокирован: MCP вернул результаты с высоким риском ошибки. " +
        "Нужно проверить источник вручную.",
    };
  }

  if (!provisions.length) {
    return {
      status: "no_result",
      text: "Не удалось собрать правовую позицию по вопросу.",
    };
  }

  const lines = provisions.slice(0, 5).map((item, index) => {
    return `${index + 1}. ${item.document_title}, статья/раздел ${item.provision_ref}: ${item.title || "без названия"}`;
  });

  return {
    status: "draft",
    text:
      `По вопросу: "${question}"\n\n` +
      `MCP нашел следующие возможные нормы:\n\n` +
      lines.join("\n") +
      `\n\nЭто черновик для проверки. Перед рабочим использованием нужно сверить нормы с официальным источником.`,
  };
}

function getErrorMessage(error, fallback) {
  const message = error?.message || "";

  if (message === "Failed to fetch") {
    return (
      "Браузер не смог напрямую обратиться к hosted MCP endpoint. " +
      "Чаще всего это CORS или сеть. Сам endpoint указан верно: https://russian-law-mcp.fly.dev/mcp"
    );
  }

  return message || fallback;
}

function getSearchItems(calls) {
  const searchCall = calls.find((call) => call.tool === "search_legislation");
  const results = searchCall?.result?.results;
  return Array.isArray(results) ? results : [];
}

function getProvisionItems(calls) {
  const stanceCall = calls.find((call) => call.tool === "build_legal_stance");
  const provisions = stanceCall?.result?.results?.provisions;
  return Array.isArray(provisions) ? provisions : [];
}

function getSourcesItems(calls) {
  const sourcesCall = calls.find((call) => call.tool === "list_sources");
  const result = sourcesCall?.result;
  const limit = 25;

  if (Array.isArray(result?.sources)) {
    return result.sources.slice(0, limit);
  }

  if (Array.isArray(result?.results)) {
    return result.results.slice(0, limit);
  }

  if (Array.isArray(result)) {
    return result.slice(0, limit);
  }

  return [];
}

function getItemTitle(item) {
  return (
    item.document_title ||
    item.title ||
    item.name ||
    item.source ||
    item.id ||
    "Без названия"
  );
}

function getItemMeta(item, variant) {
  const parts = [];

  if (variant === "provision" && item.provision_ref) {
    parts.push(`статья/раздел ${item.provision_ref}`);
  }

  if (item.document_id) {
    parts.push(`doc ${item.document_id}`);
  }

  if (item.score !== undefined) {
    parts.push(`score ${Number(item.score).toFixed(3)}`);
  }

  if (item.relevance !== undefined) {
    parts.push(`relevance ${Number(item.relevance).toFixed(3)}`);
  }

  if (item.type) {
    parts.push(item.type);
  }

  return parts.length ? parts.join(" · ") : "MCP result";
}

function formatMs(value) {
  const ms = Number(value);
  if (!Number.isFinite(ms)) {
    return "0 ms";
  }
  return `${ms} ms`;
}
